import { useEffect, useRef, useState } from "react";
import { FileBarChart2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { AJUDA_FINANCEIRO } from "@/lib/ajuda/dialogos/financeiro";
import { cn } from "@/lib/cn";
import { moldura } from "../estilos";
import { ImpostosECustos } from "./ImpostosECustos";

/**
 * Liga o DRE gerencial. O financeiro simples continua sendo o padrão: o DRE só presta para quem
 * lança tudo, e ligado ele muda o resultado da Visão também (um número só no app).
 */
export function DreGerencial() {
  const utils = trpc.useUtils();
  const pode = usePermissao("finance.manage");
  const config = trpc.financeiro.configFinanceira.ler.useQuery().data;
  const [confirmando, setConfirmando] = useState<"ligar" | "desligar" | null>(null);
  const quadro = useRef<HTMLElement>(null);
  // Chegar pelo convite da Visão (#dre-gerencial) rola até aqui.
  useEffect(() => {
    if (config && window.location.hash === "#dre-gerencial") quadro.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [config]);
  const salvar = trpc.financeiro.configFinanceira.salvar.useMutation({
    onSuccess: (_r, v) => {
      toast.success(v.dreLigado ? "DRE ligado. A aba DRE já está no financeiro." : "DRE desligado.");
      utils.financeiro.invalidate();
      setConfirmando(null);
    },
    onError: (e) => toast.error(e.message),
  });
  if (!config) return null;
  const alternar = (dreLigado: boolean) =>
    salvar.mutate({ dreLigado, regime: config.regime, aliquotaSobreFaturamentoBps: config.aliquotaSobreFaturamentoBps, custoDoMaterial: config.custoDoMaterial });

  return (
    <section ref={quadro} id="dre-gerencial" className={cn(moldura, "scroll-mt-20 overflow-hidden")}>
      <div className="flex flex-wrap items-start gap-3 px-4 py-4">
        <FileBarChart2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-600 dark:text-primary" />
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-900 dark:text-foreground">
            DRE gerencial
            {config.dreLigado && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">Ligado</span>}
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            {config.dreLigado
              ? "O resultado em linhas, com impostos, custo dos serviços e despesas. Vale também para a Visão do mês."
              : "Para quem lança todas as entradas e saídas: o resultado organizado em linhas, com o percentual de cada uma sobre a receita. Sem ele, o financeiro segue simples, como está."}
          </p>
        </div>
        {pode &&
          (config.dreLigado ? (
            <button type="button" onClick={() => setConfirmando("desligar")} className="h-9 rounded-md px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
              Desligar
            </button>
          ) : (
            <button type="button" onClick={() => setConfirmando("ligar")} className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700">
              Ligar o DRE
            </button>
          ))}
      </div>
      {config.dreLigado && <ImpostosECustos key={JSON.stringify(config)} config={config} pode={pode} />}

      <Dialog open={!!confirmando} onOpenChange={(v) => !v && setConfirmando(null)}>
        <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
          <DialogHeader>
            <DialogTitle ajuda={AJUDA_FINANCEIRO.ligarDre}>{confirmando === "ligar" ? "Ligar o DRE?" : "Desligar o DRE?"}</DialogTitle>
            <DialogDescription>
              {confirmando === "ligar"
                ? "O resultado da Visão do mês passa a seguir as regras do DRE: o imposto sobre o faturamento entra pela alíquota até o real ser lançado, e o material segue uma regra só. Nenhum lançamento muda, e dá para desligar depois."
                : "A aba DRE some, e a Visão do mês volta às regras do financeiro simples (sem imposto estimado). Nenhum lançamento muda, e a configuração fica guardada."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setConfirmando(null)} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
              Cancelar
            </button>
            <button
              type="button"
              disabled={salvar.isPending}
              onClick={() => alternar(confirmando === "ligar")}
              className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
            >
              {confirmando === "ligar" ? "Ligar o DRE" : "Desligar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
